'use client';

import { Bell, ArrowRight } from 'lucide-react';
import { trackCtaClick } from '@/lib/analytics';

/**
 * Alert24 product CTA — funnels InventiveHQ traffic to Alert24 (alert24.net),
 * Sean's uptime / SSL-cert / domain / log monitoring SaaS.
 *
 * Bundle-safe: uses a plain <a> tag (NOT next/link) so it can be imported into
 * R2-bundled tools like certificate-transparency-lookup. See CLAUDE.md
 * "R2 Tool Bundle System".
 *
 * Destination + UTM are centralized in buildAlert24Url() — the single swap point
 * if we later route through trafficloopback.com or a dedicated landing/signup URL.
 */

const ALERT24_BASE = 'https://alert24.net';

export type Alert24Variant = 'domain-monitor' | 'uptime' | 'incident' | 'log-search';
export type Alert24Medium = 'tool_cta' | 'blog_cta' | 'kb_cta';

interface Alert24CTAProps {
  /** Which product angle to pitch (controls copy + utm_campaign). */
  variant?: Alert24Variant;
  /** Domain to prefill (domain-monitor variant) — also interpolated into copy. */
  domain?: string;
  /** Source page slug, for utm_content + click tracking. */
  source: string;
  /** Content type hosting the CTA — sets utm_medium so attribution can compare conversion by content type. */
  medium?: Alert24Medium;
  /** Tighter spacing when embedded mid-result. */
  compact?: boolean;
}

const CAMPAIGN: Record<Alert24Variant, string> = {
  'domain-monitor': 'ct-domain-monitor',
  uptime: 'uptime-monitoring',
  incident: 'incident-oncall',
  'log-search': 'log-monitoring',
};

/** Message-matched landing page per variant (alert24.net/features/*). */
const LANDING_PATH: Record<Alert24Variant, string> = {
  'domain-monitor': '/features/domain-monitoring',
  uptime: '/features/uptime-monitoring',
  incident: '/features/incident-alerting',
  'log-search': '/features/log-monitoring',
};

/** Centralized destination builder — swap this to re-route all Alert24 CTAs. */
export function buildAlert24Url(
  variant: Alert24Variant,
  source: string,
  domain?: string,
  medium: Alert24Medium = 'tool_cta'
): string {
  const params = new URLSearchParams({
    utm_source: 'inventivehq',
    utm_medium: medium,
    utm_campaign: CAMPAIGN[variant],
    utm_content: source,
  });
  // /features/domain-monitoring consumes ?domain= to render a personalized
  // "Monitor {domain}" panel that carries straight into signup.
  if (domain) params.set('domain', domain);
  return `${ALERT24_BASE}${LANDING_PATH[variant]}?${params.toString()}`;
}

function copyFor(variant: Alert24Variant, domain?: string) {
  const d = domain || 'your domain';
  switch (variant) {
    case 'domain-monitor':
      return {
        eyebrow: 'Automate this check',
        title: `Get alerted when a new certificate appears for ${d}`,
        body:
          'Alert24’s Domain Monitor watches Certificate Transparency logs around the clock and notifies you the moment a new TLS certificate is issued — catching mis-issuance, shadow IT, and phishing prep automatically. Includes SSL & domain-expiry and DNS-change alerts.',
        button: `Monitor ${d} with Alert24`,
      };
    case 'uptime':
      return {
        eyebrow: 'Measure it in production',
        title: 'Track MTTR and uptime on your real systems',
        body:
          'Alert24 monitors uptime, response time, and on-call response so you can measure — and shrink — MTTR on the systems you actually run.',
        button: 'Start monitoring with Alert24',
      };
    case 'incident':
      return {
        eyebrow: 'Route alerts to your team',
        title: 'Turn webhooks into on-call alerts',
        body:
          'Alert24 turns your webhooks into on-call schedules, escalation policies, and incident timelines — so the right person actually gets paged.',
        button: 'Try Alert24',
      };
    case 'log-search':
      return {
        eyebrow: 'Stop tailing logs by hand',
        title: 'Get alerted when your logs go wrong',
        body:
          'Alert24’s lightweight agent watches your log files where they live and alerts on error spikes, pattern matches, log floods, and sudden silence — no log shipping, no SIEM bill, no per-GB ingest pricing.',
        button: 'Try Alert24 log monitoring',
      };
  }
}

export default function Alert24CTA({ variant = 'domain-monitor', domain, source, medium = 'tool_cta', compact }: Alert24CTAProps) {
  const c = copyFor(variant, domain);
  const href = buildAlert24Url(variant, source, domain, medium);

  return (
    <div
      className={`${compact ? 'p-4' : 'p-5'} rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-sky-50`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-indigo-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-1">
            {c.eyebrow}
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">{c.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{c.body}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick(`Alert24: ${variant}`, medium, href)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
          >
            {c.button}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
